import { macroCondition, isTesting } from '@embroider/macros';
import { registerDestructor } from '@ember/destroyable';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { next } from '@ember/runloop';
import Feed from './feed';
export default class BroadcastFeed extends Feed {
  kind = 'broadcast'

  @tracked nowPlayingRecord;

  constructor(nowPlayingObject, options = {}) {
    super(nowPlayingObject, options);
    this.nowPlayingRecord = nowPlayingObject;
    this.startTask.perform();
    registerDestructor(this, this.teardown.bind(this));
  }

  get isLive() {
    return true; // broadcast feed = live feed
  }

  get imageUrl() {
    return this.show?.image?.medium_square?.url || this.airing?.image?.medium_square?.url
  }

  get url() {
    return this.nowPlayingRecord?.streamUrl;
  }

  get canPlay() {
    return !!this.url
  }

  @task
  *startTask() {
    if (this.settings.debug) {
      console.log(`built broadcast feed for nowPlayingRecord ${this.nowPlayingRecord.id}, starting monitors`);
    }

    try {
      yield this.updateAttributesTask.perform(this.nowPlayingRecord) // initial load
      yield this.loadCableTask.perform();
    } catch (e) {
      console.error(e)
      //no op
    }
    this.loopTask.perform();
    next(() => this.isLoaded = true)
  }

  @task
  *loopTask() { 
    if (macroCondition(isTesting())) {
      return;
    }
    while (true) {
      try {
        if (this.isLoaded && !this.cableConnection?.connected) {
          yield timeout(10000);
          yield this.loadNowPlayingDataTask.perform();
        } else if (this.isLoaded && this.cableConnection?.connected) {
          yield timeout(60000);
          yield this.loadNowPlayingDataTask.perform();
        } else {
          yield timeout(1000);
        }
      } catch (e) {
        //no op
        yield timeout(10000);
      }
    }
  }

  get cableConnected() {
    return this.cableConnection?.connected
  }

  @task({ drop: true })
  *loadNowPlayingDataTask() {
    let nowPlaying = yield this.store.queryRecord('now-playing', {
        id: this.broadcastId,
        stationId: this.station.uid,
        cache: new Date().getTime(),
        reload: true
      })
      .catch((e) => {
        console.error(e);
      });


    if (nowPlaying) {
      yield this.updateAttributesTask.perform(nowPlaying);
      if (nowPlaying.updatedAt != this.nowPlayingUpdatedAt) {
        this.nowPlayingUpdatedAt = nowPlaying.updatedAt
        if (this.itemsFetched) {
          this.loadAiringItemsTask.perform()
        }
      }
    }
  }

  @task({ restartable: true })
  *playTask() {
    let sound;
    if (!this.loadedSound) {
      try {
        var results = yield this.stereo.playTask.perform((this.url), {
          useConnections: ['NativeAudio', 'Howler'],
          metadata: { airing: this.airing },
          xhr: {
            headers: this.session?.authenticatedHeaders,
          },
        });
      } catch (e) {
        throw new Error('failed to load sound', e);
      }
      sound = results?.sound;
    }

    if (this.loadedSound) {
      if (!this.loadedSound.isPlaying && this.loadedSound) {
        yield this.loadedSound.togglePause();
      } else {
        // TODO: figure out better queuing
      }
    }

    yield timeout(200);
  }

  @task
  *loadCableTask() {
    this.cableConnection = this.socket.subscribe(
      {
        channel: 'NowPlayingChannel',
        station_id: this.station.id,
        broadcast_id: this.broadcastId,
      },
      {
        received: (data) => {
          if (this.settings.debug) {
            console.log(
              '⚡️ received NowPlayingChannel update for stream monitor, handling...'
            );
          }
          this.lastCacheKey = data?.cache;
          this.loadNowPlayingDataTask.perform();
        },
      }
    );
  }

  @task
  *updateAttributesTask(nowPlaying) {
    this.station = this.currentNowPlayingModel(nowPlaying, 'station')
    this.broadcast = this.currentNowPlayingModel(nowPlaying, 'broadcast')
    this.airing = this.currentNowPlayingModel(nowPlaying, 'airing')
    this.show = this.currentNowPlayingModel(nowPlaying, 'show')
    this.stream = this.currentNowPlayingModel(nowPlaying, 'stream')
    this.track = this.currentNowPlayingModel(nowPlaying, 'track')
    this.traffic = this.currentNowPlayingModel(nowPlaying, 'traffic')
    this.note = this.currentNowPlayingModel(nowPlaying, 'note')

    let item = _.maxBy(_.compact([this.track, this.traffic, this.note]), (item) => new Date(item.playedAt).getTime())
    this.item = item;
    this.itemType = item?.type;

    this.feeds.updateAudioMetadata.perform(this.url, {
      station: this.station,
      broadcast:this.broadcast,
      airing: this.airing,
      show: this.show,
      stream: this.stream,
      track: this.track,
      note: this.note,
      traffic: this.traffic
    })
  }

  currentNowPlayingModel(nowPlayingRecord, modelName) {
    let id = nowPlayingRecord.belongsTo(modelName)?.id()

    if (id) {
      return this.store.peekRecord(modelName, id)
    }
  }

  teardown() {
    this.cableConnection?.unsubscribe()

    console.log('destroyed broadcast feed', this.url)
  }
}
